import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { translations } from './translations';

// Language preference for portal users (Pickers / Pressers / Packers).
// Set by admin in Settings → Pickers/Pressers/Packing Users tab and
// returned by /api/pressing/verify-pin in the response payload. Persisted
// in sessionStorage so the kiosk-lock + re-PIN flow can restore it before
// the next render. Defaults to 'en' for any non-portal route or when the
// PIN-verify response doesn't carry a language.
const STORAGE_KEY = 'opshub_portal_language';

const I18nContext = createContext({
    language: 'en',
    setLanguage: () => {},
    t: (k) => k,
});

export function I18nProvider({ children }) {
    const [language, setLanguageState] = useState(() => {
        try {
            const v = sessionStorage.getItem(STORAGE_KEY);
            return v === 'es' ? 'es' : 'en';
        } catch { return 'en'; }
    });

    // Public setter — also persists. Components that learn the language at
    // PIN-verify time should call this so the rest of the portal renders
    // in the user's preferred language.
    const setLanguage = useCallback((lang) => {
        const v = (lang === 'es') ? 'es' : 'en';
        try { sessionStorage.setItem(STORAGE_KEY, v); } catch {}
        setLanguageState(v);
    }, []);

    // Translator. t(key) → English by default; t(key, { name: 'X' }) → interpolates
    // {name}. If a key is missing, returns the English fallback if present, else
    // the raw key (so a typo or missing string is visible during dev rather than
    // silently rendering blank).
    const t = useMemo(() => {
        return (key, vars) => {
            const dict = translations[language] || translations.en || {};
            let s = dict[key];
            if (s == null) s = (translations.en || {})[key];
            if (s == null) s = key;
            if (vars && typeof s === 'string') {
                Object.keys(vars).forEach(k => {
                    s = s.split(`{${k}}`).join(vars[k] != null ? String(vars[k]) : '');
                });
            }
            return s;
        };
    }, [language]);

    // Re-sync from sessionStorage on focus — rare edge case where another
    // tab updated the kiosk's language (e.g. admin changed it remotely and
    // the user re-PINned in another tab). Cheap and harmless.
    useEffect(() => {
        const onFocus = () => {
            try {
                const v = sessionStorage.getItem(STORAGE_KEY);
                if (v === 'es' || v === 'en') setLanguageState(v);
            } catch {}
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    return (
        <I18nContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useT() {
    return useContext(I18nContext).t;
}

export function useI18n() {
    return useContext(I18nContext);
}

// Imperative helper for outside-of-component contexts (e.g. inside an
// async fetch handler that doesn't have a React component to read context
// from). Reads sessionStorage directly. Use sparingly — prefer the hook.
export function tImperative(key, vars) {
    let lang = 'en';
    try {
        const v = sessionStorage.getItem(STORAGE_KEY);
        if (v === 'es' || v === 'en') lang = v;
    } catch {}
    const dict = translations[lang] || translations.en || {};
    let s = dict[key];
    if (s == null) s = (translations.en || {})[key];
    if (s == null) s = key;
    if (vars && typeof s === 'string') {
        Object.keys(vars).forEach(k => {
            s = s.split(`{${k}}`).join(vars[k] != null ? String(vars[k]) : '');
        });
    }
    return s;
}
